import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { CaretDown, Check, SignOut, UsersThree, Plus, User, BookOpen, Globe } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { useActiveGroup } from "../contexts/ActiveGroupContext";
import AccountModal from "./AccountModal";
import LanguagePicker from "./LanguagePicker";

export default function TopHeader() {
  const { locale, setLocale, t } = useI18n();
  const { user, logout } = useAuth();
  const { groups, activeGroup, setActiveGroupId } = useActiveGroup();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const menuRef = useRef(null);
  const groupMenuRef = useRef(null);

  // Check if this is the first login (name is exactly "User")
  useEffect(() => {
    if (user && user.name === "User") {
      setAccountModalOpen(true);
    }
  }, [user]);

  const titleMap = {
    "/app": t("nav_my_tips"),
    "/app/groups": t("nav_groups"),
    "/app/leaderboard": t("nav_leaderboard"),
    "/app/rules": t("nav_rules"),
    "/app/standings": t("nav_standings"),
  };
  const currentTitle = Object.entries(titleMap).find(([k]) => location.pathname === k || location.pathname.startsWith(k + "/"))?.[1] || t("app_name");

  const toggleLocale = () => setLocale(locale === "en" ? "ru" : "en");
  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (groupMenuRef.current && !groupMenuRef.current.contains(e.target)) setGroupMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const initials = (user?.name || user?.email || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  const selectorLabel = activeGroup?.name || (groups.length > 0 ? t("select_group") : t("no_active_group"));

  return (
    <>
      <header className="sticky top-0 z-40 bg-white border-b border-neutral-200" data-testid="top-header">
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.png" alt="TippTop" className="w-full h-full object-cover" />
            </div>
          <div className="flex flex-col leading-none min-w-0">
            <span className="font-display text-[15px] text-[#02371b] truncate">{currentTitle}</span>
            <div className="relative" ref={groupMenuRef}>
              <button
                type="button"
                data-testid="group-selector"
                onClick={() => setGroupMenuOpen((o) => !o)}
                className="flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase text-neutral-500 hover:text-[#02371b] mt-0.5 max-w-[180px]"
              >
                <span className="truncate">{selectorLabel}</span>
                <CaretDown size={10} weight="bold" />
              </button>
              {groupMenuOpen && (
                <div
                  className="absolute left-0 top-full mt-2 w-64 bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden z-50"
                  data-testid="group-selector-menu"
                >
                  <div className="max-h-72 overflow-y-auto">
                    {groups.length === 0 ? (
                      <div className="p-4 text-xs text-neutral-500">{t("no_groups_title")}</div>
                    ) : (
                      groups.map((g) => {
                        const active = g.group_id === activeGroup?.group_id;
                        return (
                          <button
                            key={g.group_id}
                            type="button"
                            data-testid={`group-selector-item-${g.group_id}`}
                            onClick={() => {
                              setActiveGroupId(g.group_id);
                              setGroupMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-neutral-50 ${active ? "bg-neutral-50" : ""}`}
                          >
                            <div className="w-7 h-7 rounded-full bg-[#41a42b]/10 text-[#084d25] flex items-center justify-center flex-shrink-0 overflow-hidden border border-neutral-200">
                              {g.tournament?.crest_url ? (
                                <img
                                  src={g.tournament.crest_url}
                                  alt=""
                                  className="w-full h-full object-contain p-0.5"
                                />
                              ) : (
                                <UsersThree size={14} weight="fill" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-bold text-[#02371b] truncate">{g.name}</div>
                              {g.tournament?.name && (
                                <div className="text-[10px] text-neutral-500 truncate">{g.tournament.name}</div>
                              )}
                            </div>
                            {active && <Check size={14} weight="bold" className="text-[#41a42b]" />}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid="group-selector-manage"
                    onClick={() => {
                      setGroupMenuOpen(false);
                      navigate("/app/groups");
                    }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-xs font-bold tracking-wider uppercase text-[#02371b] border-t border-neutral-100 hover:bg-neutral-50"
                  >
                    <Plus size={14} weight="bold" /> {t("manage_groups")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <LanguagePicker variant="light" />
          {user && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                data-testid="user-menu-btn"
                onClick={() => setMenuOpen((o) => !o)}
                className="w-9 h-9 rounded-full overflow-hidden border-2 border-neutral-200 hover:border-[#02371b] bg-[#02371b] flex items-center justify-center transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-xs font-bold">{initials}</span>
                )}
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white border border-neutral-200 rounded-2xl shadow-xl overflow-hidden z-50 flex flex-col"
                  data-testid="user-menu"
                >
                  <div className="p-3 border-b border-neutral-100 bg-neutral-50/50">
                    <div className="font-bold text-sm text-[#02371b] truncate">{user.name}</div>
                    <div className="text-[11px] text-neutral-500 truncate">{user.email}</div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setAccountModalOpen(true);
                      setMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 text-left"
                  >
                    <User size={18} />
                    {t("account_settings")}
                  </button>

                  <Link
                    to="/app/rules"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-700 hover:bg-neutral-50 text-left border-t border-neutral-100"
                  >
                    <BookOpen size={18} />
                    {t("nav_rules")}
                  </Link>



                  <button
                    type="button"
                    data-testid="logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 text-left border-t border-neutral-100"
                  >
                    <SignOut size={18} weight="bold" /> {t("logout")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
    <AccountModal 
      isOpen={accountModalOpen} 
      onClose={() => setAccountModalOpen(false)} 
      requireSetup={user?.name === "User"} 
    />
    </>
  );
}
